// monitoring-ui/components/OverviewTab.tsx
import React from 'react';
import {
  MetricCard, card, InsightBanner, SectionLabel,
  Sparkline, Mono, badgeStyle, dotColor,
} from './MonitoringUi';
import type { DeploymentSummary, CostSummary, WSAlertMessage } from '../Model/Monitoring';

interface Props {
  deployments:  DeploymentSummary[];
  costSummary:  CostSummary | null;
  activeAlerts: WSAlertMessage[];
  onTabChange:  (tab: 'deployments' | 'costs' | 'ai' | 'alerts') => void;
}

const CPU_SPARK = [28, 35, 42, 38, 55, 31, 87];

export const OverviewTab: React.FC<Props> = ({
  deployments, costSummary, activeAlerts, onTabChange,
}) => {
  const healthy  = deployments.filter(d => d.instance_state === 'running' && d.active_alert_count === 0).length;
  const warning  = deployments.filter(d => d.active_alert_count > 0 && d.active_alert_count < 2).length;
  const critical = deployments.filter(d => d.active_alert_count >= 2).length;
  const totalAlerts = activeAlerts.length;

  const criticalDep = deployments.find(d => d.active_alert_count >= 2);

  return (
    <div>
      {/* Header metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
        <MetricCard
          label="Deployments"
          value={deployments.length}
          sub={`${healthy} healthy · ${warning} warn · ${critical} crit`}
        />
        <MetricCard
          label="Avg uptime"
          value="99.7%"
          valueColor="#22c55e"
          sub={<span style={{ color: '#22c55e' }}>↑ 0.2% vs last week</span>}
        />
        <MetricCard
          label="MTD cost"
          value={costSummary ? `$${costSummary.mtd_total.toLocaleString()}` : '—'}
          sub={costSummary ? `Projected $${costSummary.projected_eom.toLocaleString()}` : undefined}
        />
        <MetricCard
          label="Active alerts"
          value={totalAlerts}
          valueColor={totalAlerts > 0 ? '#f43f5e' : '#22c55e'}
          sub={`${activeAlerts.filter(a => a.severity === 'critical').length} critical · ${activeAlerts.filter(a => a.severity === 'warning').length} warning`}
        />
      </div>

      {/* AI insight banner — only shown when there's a critical deployment */}
      {criticalDep && (
        <InsightBanner>
          <strong style={{ color: '#c7d2fe' }}>AI insight:</strong>{' '}
          <Mono color="#c7d2fe">{criticalDep.app_name}</Mono> has elevated alerts —
          memory or CPU may be saturated.{' '}
          <span
            style={{ textDecoration: 'underline', cursor: 'pointer', color: '#818cf8' }}
            onClick={() => onTabChange('ai')}
          >
            Review pending fix ↗
          </span>
        </InsightBanner>
      )}

      {/* Health distribution — full width until AI activity API is available */}
      <div style={card}>
        <SectionLabel>Deployment health</SectionLabel>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          {[
            { label: 'Healthy',  count: healthy,  color: '#22c55e', bg: 'rgba(34,197,94,.06)',  border: 'rgba(34,197,94,.15)' },
            { label: 'Warning',  count: warning,  color: '#eab308', bg: 'rgba(234,179,8,.06)',  border: 'rgba(234,179,8,.15)' },
            { label: 'Critical', count: critical, color: '#f43f5e', bg: 'rgba(244,63,94,.06)',  border: 'rgba(244,63,94,.15)' },
          ].map(item => (
            <div
              key={item.label}
              style={{
                flex: 1, textAlign: 'center' as const,
                background: item.bg, border: `1px solid ${item.border}`,
                borderRadius: '6px', padding: '10px 0', cursor: 'pointer',
              }}
              onClick={() => onTabChange('deployments')}
            >
              <div style={{ fontSize: '20px', fontWeight: 600, color: item.color }}>{item.count}</div>
              <div style={{ fontSize: '10px', color: item.color, marginTop: '2px' }}>{item.label}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: '11px', color: '#52525f', marginBottom: '6px' }}>CPU usage (7d)</div>
        <Sparkline values={CPU_SPARK} height={48} />
      </div>

      {/*
        ── AI activity panel — commented out pending API implementation ─────────
        Once GET /monitoring/ai-activity (or equivalent) is available, uncomment
        and wire real data in place of the static array below.

        <div style={card}>
          <SectionLabel>Recent AI activity</SectionLabel>
          {[
            { dot: '#eab308', title: 'app-prod-3 resize awaiting approval',        meta: 'Today 13:47 · requires action' },
            { dot: '#22c55e', title: 'Auto-scaled app-prod-2 (4 → 6 containers)',   meta: 'Today 14:22 · auto-applied' },
            { dot: '#22c55e', title: 'Deployed client-acme-prod v2.3.1',           meta: 'Today 09:15 · blue/green' },
            { dot: '#818cf8', title: 'Rightsized staging-env → t3.small',          meta: 'Yesterday 22:00 · saves $18/mo' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < 3 ? '1px solid #1a1a22' : 'none' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.dot, flexShrink: 0, marginTop: '4px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '2px', color: '#aaa4a4' }}>{item.title}</div>
                <div style={{ fontSize: '10.5px', color: '#fcfcfc' }}>{item.meta}</div>
              </div>
            </div>
          ))}
          <button
            style={{ background: 'transparent', border: '1px solid #26262f', color: '#8e8ea8', borderRadius: '6px', padding: '5px 12px', fontSize: '11.5px', cursor: 'pointer', width: '100%', marginTop: '10px' }}
            onClick={() => onTabChange('ai')}
          >
            View all AI activity ↗
          </button>
        </div>
        ── end AI activity ────────────────────────────────────────────────────── */}
    </div>
  );
};